import { useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Autocomplete, IconButton, Alert, CircularProgress, Box,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createStockTransfer, type CreateStockTransferPayload } from '../../api/stocuri'
import { listWarehouses } from '../../api/depozite'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const lineSchema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  quantity: z.string().min(1, 'Câmp obligatoriu'),
  lotNumber: z.string().optional(),
})
const schema = z.object({
  transferDate: z.string().min(1, 'Câmp obligatoriu'),
  sourceWarehouseId: z.string().min(1, 'Selectați depozitul sursă'),
  destinationWarehouseId: z.string().min(1, 'Selectați depozitul destinație'),
  notes: z.string().optional(),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
}).refine((v) => v.sourceWarehouseId !== v.destinationWarehouseId || !v.sourceWarehouseId, {
  message: 'Depozitul sursă și cel de destinație trebuie să fie diferite.', path: ['destinationWarehouseId'],
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { transferDate: new Date().toISOString().slice(0, 10), sourceWarehouseId: '', destinationWarehouseId: '', notes: '', lines: [] }

export default function StockTransferFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })

  useEffect(() => { if (open) reset(EMPTY) }, [open, reset])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: CreateStockTransferPayload = {
        transferDate: values.transferDate, sourceWarehouseId: values.sourceWarehouseId,
        destinationWarehouseId: values.destinationWarehouseId, notes: values.notes || null,
        lines: values.lines.map((l) => ({ articleId: l.articleId, quantity: Number(l.quantity), lotNumber: l.lotNumber || null })),
      }
      return createStockTransfer(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stocuri', 'transferuri-stoc'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Transfer de stoc nou — între puncte de lucru</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="transferDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data transferului" sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.transferDate} helperText={errors.transferDate?.message} />
              )} />
              <Controller name="sourceWarehouseId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={warehouses?.map((w) => w.id) ?? []}
                  getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Depozit sursă" error={!!errors.sourceWarehouseId} helperText={errors.sourceWarehouseId?.message} />}
                />
              )} />
              <Controller name="destinationWarehouseId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={warehouses?.map((w) => w.id) ?? []}
                  getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Depozit destinație" error={!!errors.destinationWarehouseId} helperText={errors.destinationWarehouseId?.message} />}
                />
              )} />
            </Stack>

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`lines.${i}.articleId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
                    getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.lines?.[i]?.articleId} helperText={errors.lines?.[i]?.articleId?.message} />}
                  />
                )} />
                <Controller name={`lines.${i}.quantity`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Cantitate" sx={{ width: 140 }}
                    error={!!errors.lines?.[i]?.quantity} helperText={errors.lines?.[i]?.quantity?.message} />
                )} />
                <Controller name={`lines.${i}.lotNumber`} control={control} render={({ field }) => (
                  <TextField {...field} label="Lot (opțional)" sx={{ width: 160 }} />
                )} />
                <IconButton onClick={() => lines.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Box>
              <Button size="small" startIcon={<Add />} onClick={() => lines.append({ articleId: '', quantity: '', lotNumber: '' })}>Adaugă linie</Button>
            </Box>

            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Observații" fullWidth multiline minRows={2} />
            )} />
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Trimite transferul
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
